import React, { FunctionComponent, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import i18next from 'i18next';

import useTypedSelector from 'hooks/useTypedSelector';
import { onGetForm, onGetOne, onSave } from 'redux/actions/reservations.actions';
import {
  reservationsFormSelector,
  reservationsCementeriesSelector,
  reservationsCivilStatesSelector,
  reservationsFilesSelector,
  reservationsIdCardTypesSelector,
} from 'redux/selectors/reservations.selector';
import { userSelector } from 'redux/selectors/auth.selector';
import { isClienteRole } from 'helpers/roles';
import { history } from 'helpers/history';

import LayoutContainer from 'containers/LayoutContainer';
import ReservationsForm from 'views/public/ReservationsView/components/ReservationsForm';
import Loading from 'components/commons/Loading';

import { IReservationsFormData } from 'views/public/ReservationsView/components/ReservationsForm/types';

import 'views/public/ReservationsView/components/ReservationsForm/i18n';

const ReservationsFormContainer: FunctionComponent = () => {
  const dispatch = useDispatch();
  const { id = '' } = useParams<{ id?: string }>();
  const initialValues = useTypedSelector(reservationsFormSelector);
  const { loading } = useTypedSelector((state) => state.reservations);
  const cementeries = useTypedSelector(reservationsCementeriesSelector);
  const civilStates = useTypedSelector(reservationsCivilStatesSelector);
  const idCardTypes = useTypedSelector(reservationsIdCardTypesSelector);
  const files = useTypedSelector(reservationsFilesSelector);
  const isEditing = !!id;

  useEffect(() => {
    dispatch(onGetForm());
    if (id) dispatch(onGetOne(id));
    return () => {
      dispatch(onGetOne(''));
    };
  }, [dispatch, id]);

  const onSubmit = (formData: IReservationsFormData) => dispatch(onSave(formData, id));

  const user = useTypedSelector(userSelector);
  const item = useTypedSelector((state) => state.reservations.selectedItem);

  useEffect(() => {
    if (!loading && isEditing && item) {
      const isClient = isClienteRole(user);
      if (isClient && item.state !== 'OPENED') {
        history.push(`/public/procedures/forms/reservations/${id}/show`);
      }
    }
  }, [loading, isEditing, item, user, id]);

  const isClient = isClienteRole(user);
  const waitingForData = isEditing && !item;
  const isUnauthorized = isEditing && isClient && item && item.state !== 'OPENED';

  if (loading || waitingForData || isUnauthorized) {
    return (
      <LayoutContainer isPublic title={i18next.t<string>('PUBLIC_PROCEDURES_VIEW:TITLE')}>
        <Loading isModalMode message={i18next.t('RESERVATIONS_FORM:LOADING')} />
      </LayoutContainer>
    );
  }

  return (
    <LayoutContainer isPublic title={i18next.t<string>('PUBLIC_PROCEDURES_VIEW:TITLE')}>
      <ReservationsForm
        cementeries={cementeries}
        civilStates={civilStates}
        files={files}
        idCardTypes={idCardTypes}
        initialValues={initialValues}
        isEditing={isEditing}
        onSubmit={onSubmit}
      />
      {loading && <Loading isModalMode message={i18next.t('RESERVATIONS_FORM:LOADING')} />}
    </LayoutContainer>
  );
};

export default ReservationsFormContainer;
